import React, { useCallback, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { companyService } from '../../services/apiService';
import { useChartTheme } from '../chartTheme';
import { ErrorState, LoadingState } from '../PageState';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatDate, formatNumber, formatShortDate } from '../../utils/format';
import { parsePlainDate } from './companyFormat';
import { useCompanyList } from './companyHooks';

const WEEKS = 12;
/** The benchmark line: this share of every user setting whereabouts on every working day. */
const BENCHMARK = 0.8;

/** "3 to 9 Aug 2026", "28 Jul to 3 Aug 2026", or "29 Dec 2025 to 4 Jan 2026". */
const weekRange = (start, end) => {
  if (!start || !end) return formatDate(start || end);
  if (start.getFullYear() !== end.getFullYear()) return `${formatDate(start)} to ${formatDate(end)}`;
  if (start.getMonth() !== end.getMonth()) return `${formatShortDate(start)} to ${formatDate(end)}`;
  return `${start.getDate()} to ${formatDate(end)}`;
};

/** The Analytics section: whereabouts set each week against the most the company could set. */
const CompanyAnalytics = ({ companyId, totalUsers, api }) => {
  const theme = useChartTheme();
  const isMobile = useIsMobile();
  const stats = useCompanyList(useCallback(async () => {
    const response = await api(() => companyService.getWhereaboutsWeeklyStats(companyId, WEEKS));
    return response.data.map((item) => {
      const start = parsePlainDate(item.weekStart);
      const end = parsePlainDate(item.weekEnd);
      return { week: formatShortDate(start), count: item.count, start, end };
    });
  }, [api, companyId]));

  const { reload } = stats;
  useEffect(() => {
    reload();
  }, [reload]);

  const users = totalUsers || 0;
  const possible = users * 5;
  const benchmark = Math.round(possible * BENCHMARK);
  const yMax = Math.ceil(possible * 1.1);

  let body;
  if (stats.error) {
    body = <ErrorState message="Could not load the whereabouts figures." onRetry={stats.reload} />;
  } else if (!stats.loaded) {
    body = <LoadingState label="Loading analytics" />;
  } else if (stats.data.length === 0) {
    body = <div className="p-4 text-center text-muted">No whereabouts set in the last {WEEKS} weeks.</div>;
  } else {
    body = (
      <>
        <p className="mb-3 text-muted small">
          80% of possible days is {formatNumber(benchmark)} whereabouts a week
          ({formatNumber(users)} users × 5 working days × 80%).
        </p>
        <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
          <LineChart
            data={stats.data}
            margin={isMobile ? { top: 5, right: 8, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid {...theme.grid} />
            <XAxis dataKey="week" {...theme.xAxis} />
            <YAxis
              {...theme.yAxis}
              domain={[0, yMax > 0 ? yMax : 'auto']}
              width={isMobile ? 48 : 60}
              tickFormatter={(value) => formatNumber(value)}
            />
            <Tooltip
              {...theme.tooltip}
              formatter={(value) => [`${formatNumber(value)} whereabouts`, 'Set']}
              labelFormatter={(label, payload) => {
                const point = payload?.[0]?.payload;
                return point ? weekRange(point.start, point.end) : label;
              }}
            />
            <Legend {...theme.legend} />
            {possible > 0 && (
              <ReferenceLine
                y={possible * BENCHMARK}
                stroke={theme.colors.context}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: '80% of possible days',
                  position: 'insideTopRight',
                  fill: theme.colors.context,
                  fontSize: 12,
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="count"
              stroke={theme.colors.emphasis}
              strokeWidth={2}
              name="Whereabouts set"
              dot={{ r: 3, fill: theme.colors.emphasis }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light">
        <h5 className="mb-0">Whereabouts set by week (last {WEEKS} weeks)</h5>
      </Card.Header>
      <Card.Body>{body}</Card.Body>
    </Card>
  );
};

export default CompanyAnalytics;
